import { Box, Typography } from '@mui/material';
import { GridFooterContainer, useGridApiRef } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import ChartCard from 'components/charts/chart-card';
import BenchmarkDataGrid from 'views/benchmark-analytics/components/benchmark-data-grid';
import { useBenchmarkFormatters } from 'views/benchmark-analytics/hooks/use-benchmark-formatters';
import { useGrowthState } from '../context/growth-context';
import { useGrowthTableColumns } from '../hooks/use-growth-table-columns';
import { GrowthTableRow } from '../types/growth-table.type';
import GrowthTableStepButtons from './growth-table-step-buttons';

const GRID_HEIGHT = 440;
const MESSAGE_ROOT = 'benchmarkAnalytics.growthPage';

const getRowId = (row: GrowthTableRow): string => row.period;

// Counts every row in the table, so it never shows a page's share of the periods.
const TableFooter: React.FC = () => {
  const { growth } = useGrowthState();
  const { formatCount } = useBenchmarkFormatters();

  return (
    <GridFooterContainer>
      <Box paddingLeft={4}>
        <Typography variant="body2">
          <FormattedMessage id={`${MESSAGE_ROOT}.table.periodsFooter`} values={{ count: formatCount(growth.periods.length) }} />
        </Typography>
      </Box>
    </GridFooterContainer>
  );
};

const GrowthTableSection: React.FC = () => {
  const { growth, farmSeries, filters } = useGrowthState();
  const columns = useGrowthTableColumns();
  const apiRef = useGridApiRef();

  const rows = useMemo(
    () =>
      growth.periods.map((period, index): GrowthTableRow => ({
        period,
        company: growth.company[index],
        market: growth.market[index],
        delta: growth.delta[index],
        farms: farmSeries.map((farm) => farm.values[index]),
      })),
    [growth, farmSeries]
  );

  return (
    <ChartCard
      title={
        <FormattedMessage
          id={`${MESSAGE_ROOT}.table.title`}
          values={{ variant: <FormattedMessage id={`${MESSAGE_ROOT}.filters.${filters.variant}`} /> }}
        />
      }
      height={GRID_HEIGHT}
      actions={<GrowthTableStepButtons apiRef={apiRef} />}
    >
      <Box height={GRID_HEIGHT}>
        <BenchmarkDataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          disableColumnSorting
          slots={{ footer: TableFooter }}
        />
      </Box>
    </ChartCard>
  );
};

export default GrowthTableSection;
